/**
 * Info Tooltip
 *
 * Lightweight shared tooltip behavior for elements annotated with tooltip roles.
 */

import { infoTooltipForm } from "./info-tooltip-form";
import { InfoTooltipListener } from "./info-tooltip-listener";

let tooltipEl: HTMLDivElement | null = null;
let activeTrigger: Element | null = null;
let showTimer: ReturnType<typeof setTimeout> | null = null;

function suppressNativeTitle(trigger: Element): void {
    const title = trigger.getAttribute("title");
    if (title) {
        trigger.setAttribute("data-title-stashed", title);
        trigger.removeAttribute("title");
    }
}

function restoreNativeTitle(trigger: Element): void {
    const stashed = trigger.getAttribute("data-title-stashed");
    if (stashed !== null) {
        trigger.setAttribute("title", stashed);
        trigger.removeAttribute("data-title-stashed");
    }
}

function isMobilePopupMode(): boolean {
    return window.matchMedia("(hover: none), (pointer: coarse)").matches;
}

function removeTooltip(): void {
    if (showTimer) {
        clearTimeout(showTimer);
        showTimer = null;
    }
    if (tooltipEl) {
        tooltipEl.remove();
        tooltipEl = null;
    }
    if (activeTrigger) {
        restoreNativeTitle(activeTrigger);
    }
    activeTrigger = null;
}

function getTooltipText(trigger: Element): string {
    const nested = infoTooltipForm.getNestedTooltipSource(trigger);
    if (nested) {
        return (nested.textContent || "").trim();
    }

    const fallbackHost = infoTooltipForm.getTooltipFallbackHost(trigger);

    const title = trigger.getAttribute("title") || trigger.getAttribute("data-title-stashed");
    if (title) {
        return title.trim();
    }

    const hostTitle = fallbackHost?.getAttribute("title") || fallbackHost?.getAttribute("data-title-stashed");
    if (hostTitle) {
        return hostTitle.trim();
    }

    const ariaLabel = trigger.getAttribute("aria-label");
    if (ariaLabel) {
        return ariaLabel.trim();
    }

    const hostAriaLabel = fallbackHost?.getAttribute("aria-label");
    if (hostAriaLabel) {
        return hostAriaLabel.trim();
    }

    const sourceId = trigger.getAttribute("data-info-tooltip-source");
    if (!sourceId) return "";

    const source = infoTooltipForm.getTooltipSource(document, sourceId);
    if (!source) return "";

    return (source.textContent || "").trim();
}

function positionTooltip(trigger: Element): void {
    if (!tooltipEl) return;

    if (tooltipEl.classList.contains("info-tooltip--mobile-popup")) {
        tooltipEl.style.left = "50%";
        tooltipEl.style.top = "auto";
        tooltipEl.style.bottom = "max(16px, env(safe-area-inset-bottom, 0px) + 16px)";
        return;
    }

    const rect = trigger.getBoundingClientRect();
    const padding = 8;
    const offsetY = 8;
    const tipRect = tooltipEl.getBoundingClientRect();

    let left = rect.left;
    let top = rect.bottom + offsetY;

    const viewportW = document.documentElement?.clientWidth || 0;
    const viewportH = document.documentElement?.clientHeight || 0;

    const maxLeft = viewportW - tipRect.width - padding;
    left = Math.max(padding, Math.min(left, maxLeft));

    const maxTop = viewportH - tipRect.height - padding;
    top = Math.max(padding, Math.min(top, maxTop));

    tooltipEl.style.left = `${left}px`;
    tooltipEl.style.top = `${top}px`;
}

function showTooltip(trigger: Element, options?: { immediate?: boolean; mobilePopup?: boolean }): void {
    const text = getTooltipText(trigger);
    if (!text) return;

    removeTooltip();
    activeTrigger = trigger;
    suppressNativeTitle(trigger);

    const renderTooltip = () => {
        const el = document.createElement("div");
        el.className = "timestamp-tooltip";
        if (options?.mobilePopup) {
            el.classList.add("info-tooltip--mobile-popup");
        }
        el.textContent = text;
        document.body.appendChild(el);

        tooltipEl = el;
        positionTooltip(trigger);
    };

    if (options?.immediate) {
        renderTooltip();
        return;
    }

    showTimer = setTimeout(renderTooltip, 500);
}

export function initInfoTooltipSystem(): void {
    if (document.documentElement.dataset.infoTooltipInit === "1") return;
    document.documentElement.dataset.infoTooltipInit = "1";

    InfoTooltipListener.register({
        onPointerOver: (event) => {
            if (isMobilePopupMode()) return;

            const target = infoTooltipForm.getEventTarget(event);
            const trigger = infoTooltipForm.getTooltipTrigger(target);
            if (!trigger) return;
            if (!getTooltipText(trigger)) return;

            if (activeTrigger === trigger) return;
            showTooltip(trigger);
        },
        onPointerOut: (event) => {
            if (!activeTrigger) return;

            const relatedTarget = event instanceof MouseEvent ? event.relatedTarget : null;
            const related = relatedTarget instanceof Element ? relatedTarget : null;
            if (related && activeTrigger.contains(related)) return;
            if (tooltipEl && related && tooltipEl.contains(related)) return;

            removeTooltip();
        },
        onScroll: () => {
            if (!activeTrigger || !tooltipEl) return;
            positionTooltip(activeTrigger);
        },
        onResize: () => {
            if (!activeTrigger || !tooltipEl) return;
            positionTooltip(activeTrigger);
        },
        onKeydown: (event) => {
            if (event.key === "Escape") {
                removeTooltip();
            }
        },
        onClick: (event) => {
            const target = infoTooltipForm.getEventTarget(event);
            if (!target) return;

            if (isMobilePopupMode()) {
                const mobileTrigger = infoTooltipForm.getTooltipTrigger(target);
                const tooltipText = mobileTrigger ? getTooltipText(mobileTrigger) : "";

                if (activeTrigger && mobileTrigger === activeTrigger) {
                    event.preventDefault();
                    removeTooltip();
                    return;
                }

                if (mobileTrigger && tooltipText) {
                    event.preventDefault();
                    showTooltip(mobileTrigger, { immediate: true, mobilePopup: true });
                    return;
                }

                if (tooltipEl) {
                    removeTooltip();
                }

                return;
            }

            if (activeTrigger && infoTooltipForm.getTooltipTrigger(target) === activeTrigger) {
                removeTooltip();
                return;
            }

            if (tooltipEl) {
                removeTooltip();
            }
        },
    });
}
